import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

function NotificationsSettingsPage() {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [notificationSettings, setNotificationSettings] = useState({
    appointmentStarted: true,
    appointmentEnded: true,
    appointmentCompleted: true,
    appointmentCancelled: true,
    appointmentRescheduled: true,
    appointmentIncomplete: true,
    paymentReceived: true,
    paymentPending: true,
    browserNotifications: true,
    appointmentStartNotificationFrequency: 15,
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationSaved, setNotificationSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const loadSettings = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setNotificationSettings({
            appointmentStarted:
              data.notificationSettings?.appointmentStarted !== false,
            appointmentEnded:
              data.notificationSettings?.appointmentEnded !== false,
            appointmentCompleted:
              data.notificationSettings?.appointmentCompleted !== false,
            appointmentCancelled:
              data.notificationSettings?.appointmentCancelled !== false,
            appointmentRescheduled:
              data.notificationSettings?.appointmentRescheduled !== false,
            appointmentIncomplete:
              data.notificationSettings?.appointmentIncomplete !== false,
            paymentReceived:
              data.notificationSettings?.paymentReceived !== false,
            paymentPending: data.notificationSettings?.paymentPending !== false,
            browserNotifications:
              data.notificationSettings?.browserNotifications !== false,
            appointmentStartNotificationFrequency:
              data.notificationSettings
                ?.appointmentStartNotificationFrequency || 15,
          });
        }
      } catch (e) {
        console.error("Failed to load notification settings", e);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [user]);

  const handleNotificationSettingChange = async (setting, value) => {
    if (!user) return;

    const updatedSettings = {
      ...notificationSettings,
      [setting]: value,
    };
    setNotificationSettings(updatedSettings);

    setNotificationSaving(true);
    setNotificationSaved(false);
    try {
      const docRef = doc(db, "users", user.uid);
      await setDoc(
        docRef,
        { notificationSettings: updatedSettings },
        { merge: true }
      );
      setNotificationSaved(true);
      setTimeout(() => setNotificationSaved(false), 2000);
    } catch (e) {
      console.error("Failed to save notification settings", e);
      // Revert on error
      setNotificationSettings(notificationSettings);
    } finally {
      setNotificationSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-layout">
        <header
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            background: "white",
          }}
        >
          <button
            className="settings-gear"
            onClick={() => navigate("/settings")}
            aria-label="Back"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              fontSize: "24px",
              color: "var(--register-text)",
            }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              fontWeight: "600",
              flex: 1,
              textAlign: "center",
            }}
          >
            Notification Settings
          </h2>
          <div style={{ width: "40px" }} />
        </header>
        <div style={{ padding: "16px", textAlign: "center" }}>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "15px",
              fontWeight: "600",
            }}
          >
            Loading settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-layout">
      <header
        style={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e2e8f0",
          background: "white",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          className="settings-gear"
          onClick={() => navigate("/settings")}
          aria-label="Back"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            fontSize: "24px",
            color: "var(--register-text)",
          }}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h2
          style={{
            margin: 0,
            fontSize: "20px",
            fontWeight: "600",
            flex: 1,
            textAlign: "center",
          }}
        >
          Notification Settings
        </h2>
        <div style={{ width: "40px" }} />
      </header>

      <div
        style={{
          padding: "16px",
          maxWidth: "800px",
          margin: "0 auto",
          width: "100%",
          paddingBottom: "100px",
        }}
      >
        <section className="settings-section">
          <h3 className="settings-section-title">Appointment Notifications</h3>

          <div className="settings-card-group" style={{ marginBottom: "16px" }}>
            <div
              className="settings-notification-item no-border"
              style={{
                flexDirection: "column",
                alignItems: "stretch",
                padding: "18px 16px",
                background: "var(--card-bg, #ffffff)",
                borderRadius: "12px",
                gap: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "14px",
                }}
              >
                <div
                  className="settings-icon-box"
                  style={{ marginTop: "2px", flexShrink: 0 }}
                >
                  <span className="material-symbols-outlined">schedule</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    className="settings-label"
                    style={{
                      marginBottom: "6px",
                      fontSize: "15px",
                      fontWeight: "600",
                      lineHeight: "1.4",
                    }}
                  >
                    Appointment Start Notification
                  </p>
                  <p
                    className="settings-value"
                    style={{
                      fontSize: "13px",
                      lineHeight: "1.5",
                      fontWeight: "400",
                      color: "var(--register-muted)",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    Receive a notification before your appointment begins to
                    help you prepare and stay on schedule
                  </p>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  paddingLeft: "54px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: "20px",
                    color: "var(--primary, #06b6d4)",
                    flexShrink: 0,
                  }}
                >
                  notifications
                </span>
                <select
                  className="form-select"
                  value={
                    notificationSettings.appointmentStartNotificationFrequency
                  }
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentStartNotificationFrequency",
                      parseInt(e.target.value)
                    )
                  }
                  disabled={notificationSaving}
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1.5px solid var(--border-color, #e5e7eb)",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    background: "var(--input-bg, #f9fafb)",
                    color: "var(--text-primary, #1f2937)",
                    transition: "all 0.2s ease",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "var(--primary, #06b6d4)";
                    e.target.style.background = "#ffffff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "var(--border-color, #e5e7eb)";
                    e.target.style.background = "var(--input-bg, #f9fafb)";
                  }}
                >
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                </select>
              </div>
            </div>
          </div>

          <div className="settings-card-group">
            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">play_circle</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Started</p>
                  <p className="settings-value">
                    Notify when appointment time arrives
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentStarted}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentStarted",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">schedule</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Ended</p>
                  <p className="settings-value">
                    Notify when appointment time is over
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentEnded}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentEnded",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">
                    check_circle
                  </span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Completed</p>
                  <p className="settings-value">
                    Notify when appointment is marked complete
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentCompleted}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentCompleted",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">cancel</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Cancelled</p>
                  <p className="settings-value">
                    Notify when appointment is cancelled
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentCancelled}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentCancelled",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">
                    event_repeat
                  </span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Rescheduled</p>
                  <p className="settings-value">
                    Notify when appointment is rescheduled
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentRescheduled}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentRescheduled",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item no-border">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">warning</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Appointment Incomplete</p>
                  <p className="settings-value">
                    Notify for appointments not completed
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.appointmentIncomplete}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "appointmentIncomplete",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h3 className="settings-section-title">Payment Notifications</h3>
          <div className="settings-card-group">
            <div className="settings-notification-item">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">payments</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Payment Received</p>
                  <p className="settings-value">
                    Notify when payment is marked as paid
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.paymentReceived}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "paymentReceived",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            <div className="settings-notification-item no-border">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">pending</span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Payment Pending</p>
                  <p className="settings-value">
                    Notify when payment is marked as pending
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.paymentPending}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "paymentPending",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h3 className="settings-section-title">Browser Notifications</h3>
          <div className="settings-card-group">
            <div className="settings-notification-item no-border">
              <div className="settings-row-left">
                <div className="settings-icon-box">
                  <span className="material-symbols-outlined">
                    notifications_active
                  </span>
                </div>
                <div className="settings-row-text">
                  <p className="settings-label">Enable Browser Notifications</p>
                  <p className="settings-value">
                    Receive push notifications in your browser
                  </p>
                </div>
              </div>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={notificationSettings.browserNotifications}
                  onChange={(e) =>
                    handleNotificationSettingChange(
                      "browserNotifications",
                      e.target.checked
                    )
                  }
                  disabled={notificationSaving}
                />
                <span className="settings-toggle-slider"></span>
              </label>
            </div>

            {notificationSaved && (
              <div
                style={{
                  padding: "12px 16px",
                  background: "var(--success-light)",
                  color: "var(--success)",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: "20px" }}
                >
                  check_circle
                </span>
                Notification settings saved
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default NotificationsSettingsPage;
